import { useEffect, useRef, useState, type ReactElement } from "react";
import { Tabs, type TabEntry } from "../../ui/kit/index.ts";
import type { SettingsStore } from "../settings.ts";
import type { NameMatch } from "./lookup.ts";
import { scopeIconClass } from "./scope-icon.tsx";
import type { ChannelState, ChatLine } from "./store.ts";
import { NOTHING_SEEN, badgeText, newlyUnread, openTabs, readChatTab, scrollToShow, seeTabs, shownTab, showsLine, unreadCounts, writeChatTab, type ChatTab, type TabCounts, type TabSeen } from "./tabs.ts";
import { say } from "../../words.ts";

/**
 * The chat panel's tab row (P1.9-T41, ADR-068's amendment "scope tabs, and Help's icon"; the design's chat panel, a
 * visual reference): the kit's tabs over the log, All in words and every scope's tab its scope's icon, a scope's tab
 * named for its scope and counting what it has not shown in the kit's badge. `useChatTabs` holds the kept choice and
 * what each scope's tab has seen, and gives the panel the tab shown, the lines it shows and the row; the rules are
 * `tabs.ts`'s.
 */

/**
 * The id of the tab panel the log sits in, which every tab controls.
 */
export const CHAT_TAB_PANEL_ID = "ot-chat-tabpanel";

/**
 * A tab's element id.
 *
 * @param tab The tab.
 * @returns The id, which the tab panel is labelled by while its tab is chosen.
 */
export function chatTabId(tab: ChatTab): string {
  return `ot-chat-tab-${tab}`;
}

/**
 * A tab as the kit draws it: All in words, a scope's tab its icon from the atlas, hidden from assistive technology, with
 * `data-chat-scope` so the panel's scope tooltip names it on hover and focus; a scope's tab's name says its count.
 *
 * @param tab The tab.
 * @param counts What each scope's tab has not shown.
 * @returns The kit's entry.
 */
export function chatTabEntry(tab: ChatTab, counts: TabCounts): TabEntry<ChatTab> {
  if (tab === "all") {
    return { id: tab, label: say("chat.allTab"), name: say("chat.allTab"), data: { "data-chat-tab": tab } };
  }
  const badge = badgeText(counts[tab]);
  const name = say(`chat.scopes.${tab}`);
  return {
    id: tab,
    label: <span className={`ot-chat__tab-icon ${scopeIconClass(tab)}`} aria-hidden="true" />,
    name: badge === null ? name : say("chat.unread", { scope: name, count: badge }),
    badge,
    data: { "data-chat-tab": tab, "data-chat-scope": tab },
  };
}

/**
 * The row. It scrolls sideways once its tabs outgrow a narrow panel, and brings into view, moving no further than it
 * must, the tab chosen, the kept one as the page loads included, and the tab whose unread count just rose, so a
 * badge is never left out of sight behind the row's edge.
 *
 * @param props The `open` tabs, the tab `shown`, the `counts` of what each has not shown, and `onChoose` with a tab
 *   chosen.
 * @returns The tab list.
 */
export function ChatTabRow({ open, shown, counts, onChoose }: { readonly open: readonly ChatTab[]; readonly shown: ChatTab; readonly counts: TabCounts; readonly onChoose: (tab: ChatTab) => void }): ReactElement {
  const row = useRef<HTMLDivElement>(null);
  const before = useRef<{ counts: TabCounts; shown: ChatTab | null }>({ counts, shown: null });
  const { local, global, trade, help, party, whisper } = counts;
  useEffect(() => {
    const was = before.current;
    before.current = { counts, shown };
    const target = newlyUnread(was.counts, counts) ?? (was.shown === shown ? null : shown);
    const view = row.current;
    const tab = target === null ? null : (view?.querySelector<HTMLElement>(`[data-chat-tab='${target}']`) ?? null);
    if (view === null || tab === null) {
      return;
    }
    const box = view.getBoundingClientRect();
    const item = tab.getBoundingClientRect();
    view.scrollLeft = scrollToShow(view.scrollLeft, box.width, item.left - box.left + view.scrollLeft, item.width);
  }, [local, global, trade, help, party, whisper, shown]);
  return (
    <div ref={row} className="ot-chat__tabs" data-chat="tabs">
      <Tabs label={say("chat.tabs")} tabs={open.map((tab) => chatTabEntry(tab, counts))} value={shown} onChange={onChoose} tabId={chatTabId} panelId={CHAT_TAB_PANEL_ID} />
    </div>
  );
}

/**
 * What the panel reads of its tabs.
 */
export interface ChatTabs {
  readonly open: readonly ChatTab[];
  readonly shown: ChatTab;
  readonly lines: readonly ChatLine[];
  readonly counts: TabCounts;
  readonly choose: (tab: ChatTab) => void;
}

/**
 * The panel's tabs: the kept choice, read once from the browser's store and written on each choice; the tab shown,
 * the kept one while it is open; the lines it shows; and the counts, from what each scope's tab has seen, which
 * follows the tab shown as lines arrive.
 *
 * @param lines The log.
 * @param channels The channels as the gateway last sent them, or null before it has.
 * @param self The player's names, or null before the welcome.
 * @param store Where the choice is kept, or null for nowhere.
 * @param folded Whether the panel is minimised: no tab shows the log then, so none sees a line and every scope
 *   counts what arrives, for the bar.
 * @param party Whether the character is in a party, which opens the Party tab.
 * @returns The tabs.
 */
export function useChatTabs(lines: readonly ChatLine[], channels: ChannelState | null, self: NameMatch | null, store: SettingsStore | null, folded = false, party = false): ChatTabs {
  const [kept, setKept] = useState<ChatTab>(() => readChatTab(store));
  const [held, setHeld] = useState<TabSeen>(NOTHING_SEEN);
  const open = openTabs(channels, party);
  const shown = shownTab(kept, open);
  const seen = folded ? held : seeTabs(held, shown, lines);
  useEffect(() => {
    if (seen !== held) {
      setHeld(seen);
    }
  }, [seen]);
  return {
    open,
    shown,
    lines: shown === "all" ? lines : lines.filter((line) => showsLine(shown, line)),
    counts: unreadCounts(lines, seen, self),
    choose: (tab) => {
      setKept(tab);
      writeChatTab(tab, store);
    },
  };
}
